import { useEffect, useState } from 'react'
import { useGetDefaultFlowQuery } from 'api/app-flows-api'
import { useGetCurrentSpaceMemberQuery, useGetMemberSystemAnswersQuery, useGetSpaceQuery } from 'api/spaces-api'
import { authStorage } from 'app/auth-storage'
import { useBlobImage } from 'utils/useBlobImage'
import { Pto } from '@outcoreaix/pto'

export type StepsConfig = {
  welcomeStepNeeded: boolean
  nameDetailsNeeded: boolean
  defaultFlowNeeded: boolean
  courseDetailsNeeded: boolean
}

const useOnboardingFlow = (spaceId?: number, skip: boolean = false) => {
  const [stepsConfig, setStepsConfig] = useState<StepsConfig | null>(null)

  const { data: defaultFlow, isLoading: defaultFlowLoading } = useGetDefaultFlowQuery(undefined, { skip })
  const { data: spaceData, isLoading: spaceLoading } = useGetSpaceQuery(
    { spaceId: spaceId! },
    { skip: !spaceId || skip }
  )
  const { data: currentMember, isLoading: memberLoading } = useGetCurrentSpaceMemberQuery(
    { spaceId: spaceId! },
    { skip: !authStorage.user || !spaceId || skip }
  )
  const { data: memberAnswers, isLoading: answersLoading } = useGetMemberSystemAnswersQuery(spaceId!, {
    skip: !spaceId || skip
  })

  const isLoading = defaultFlowLoading || spaceLoading || memberLoading || answersLoading
  const blobAvatarUrl = useBlobImage(spaceData?.aiConfig?.avatarUrl || '')

  // compute steps config once all data is ready
  useEffect(() => {
    if (isLoading || stepsConfig) return

    const noAnswerForRequiredQuestions = !defaultFlow?.questions
      ?.filter((q) => q.isRequired)
      .map((q) => q.id)
      .every((id) => memberAnswers?.map((a) => a.questionId).includes(id))

    const noAnswerForQuestions = !defaultFlow?.questions
      ?.map((q) => q.id)
      .every((id) => memberAnswers?.map((a) => a.questionId).includes(id))

    const defaultFlowNeeded = !!defaultFlow
      ? noAnswerForRequiredQuestions ||
        (!currentMember?.tags.includes(Pto.Spaces.SpaceMemberTag.Inducted) && noAnswerForQuestions)
      : false

    const courseDetailsNeeded = currentMember ? !currentMember.course && defaultFlowNeeded : true
    const nameDetailsNeeded = authStorage.getPendingUserNameSet() || authStorage.userNameSetNeeded()

    setStepsConfig({
      welcomeStepNeeded: nameDetailsNeeded || defaultFlowNeeded || courseDetailsNeeded,
      nameDetailsNeeded,
      defaultFlowNeeded,
      courseDetailsNeeded
    })
  }, [isLoading, defaultFlow, memberAnswers, currentMember, stepsConfig])

  const onboardingNeeded = !!stepsConfig?.welcomeStepNeeded

  return {
    isLoading,
    onboardingNeeded,
    stepsConfig,
    defaultFlow,
    currentMember,
    memberAnswers,
    spaceData,
    blobAvatarUrl
  }
}

export default useOnboardingFlow
